"use client";

/**
 * The public pricing page.
 *
 * It did not exist. /pricing was the signed-in upgrade screen, rendered entirely on the
 * client and marked noindex precisely because a crawler got an empty shell with the generic
 * site title. Its own layout carried the note: "reverse this the day /pricing server renders
 * its plans". This is that page, and the upgrade screen has moved to /upgrade.
 *
 * The prices and limits come from lib/data/subscriptions.ts, the same file the product bills
 * from. A pricing page that keeps its own copy of the numbers is a pricing page that lies
 * about them within a quarter.
 *
 * A signed-in member who lands here is sent to /upgrade, where the real buttons are: this
 * page cannot start a Stripe or Apple purchase and should not pretend to.
 */

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Check, Minus } from "lucide-react";
import { plans } from "@/lib/data/subscriptions";
import type { SubscriptionPlan } from "@/types/subscription";
import { buttonClasses } from "@/lib/ui/buttonClasses";
import { SiteHero, SectionHead, FaqList, CtaBand, RelatedLinks } from "@/components/marketing/site-ui";
import { useSiteContent } from "@/components/marketing/use-site-content";
import enMessages from "@/messages/en.json";

const enChecks = enMessages.site.pricingPage.checkRows;
import { cn } from "@/lib/utils";

type Cycle = "monthly" | "yearly";

/**
 * Which tier gets a tick, in [free, premium, elite] order, keyed by the row id the translations
 * carry a label for. This is data, not copy: it used to live in the locale files as the strings
 * "yes" and "no", which meant twenty translators were handed two English words that had to stay
 * English or the table stopped rendering. Keeping it here also keeps it next to the plan data it
 * describes. Every id must exist in site.pricingPage.checkRows.
 */
const CHECK_ROWS: { id: keyof typeof enChecks; tiers: [boolean, boolean, boolean] }[] = [
  { id: "tickets", tiers: [true, true, true] },
  { id: "discoveryVisibility", tiers: [false, true, true] },
  { id: "contentVisibility", tiers: [false, false, true] },
  { id: "eliteSpaces", tiers: [false, false, true] },
  { id: "eliteExtras", tiers: [false, false, true] },
];

const TEXT_ROWS = ["likesYou", "analytics"] as const;

function planFor(name: string, cycle: Cycle) {
  return plans.find((plan) => plan.name === name && plan.frequency === cycle);
}

export function PricingClient() {
  const c = useSiteContent();
  const page = c.pricingPage;
  const router = useRouter();
  const [cycle, setCycle] = useState<Cycle>("monthly");

  /* Members belong on the upgrade screen, which knows their current plan and can actually
     take a payment. Replace rather than push so the back button still leaves the site.

     The auth store is imported here rather than at the top of the file. This is the public
     pricing page: almost everybody who reads it is not signed in, and a static import puts
     the Supabase browser client, 230KB, into the page for all of them so that the few who
     are signed in can be redirected. Loading it after the page has painted costs the
     redirect a moment and costs everyone else nothing. */
  useEffect(() => {
    let cancelled = false;

    /* A dynamic import alone was not enough: the effect runs for every visitor, so the chunk
       was fetched a moment later rather than not at all. Supabase keeps its session in
       localStorage under sb-<project>-auth-token, so if no such key exists this browser has
       never signed in and there is nothing to check. Reading a key costs nothing and needs
       no library. */
    const maybeSignedIn = (() => {
      try {
        return Object.keys(window.localStorage).some(
          (key) => key.startsWith("sb-") && key.endsWith("-auth-token"),
        );
      } catch {
        /* Private mode, or storage blocked. Assume signed out: the worst case is a member
           reading the public pricing page instead of the upgrade screen, and every price on
           it is correct either way. */
        return false;
      }
    })();

    if (!maybeSignedIn) return;

    void (async () => {
      const { useAuthStore } = await import("@/stores/auth-store");
      if (cancelled) return;

      if (useAuthStore.getState().isAuthenticated) {
        router.replace("/upgrade");
        return;
      }

      /* A member whose session is still being restored is not authenticated yet on the tick
         above, so watch until they are, or until they leave the page. */
      const unsubscribe = useAuthStore.subscribe((state) => {
        if (state.isAuthenticated) {
          unsubscribe();
          router.replace("/upgrade");
        }
      });

      if (cancelled) unsubscribe();
    })();

    return () => {
      cancelled = true;
    };
  }, [router]);

  /* Prices and the plan names come from lib/data/subscriptions.ts, the same file the product
     bills from, so a price change reaches this page without anyone editing it. The words are
     translated content: the benefit lists in subscriptions.ts are English only, and rendering
     them here put an English bullet list on the Spanish, Arabic and Japanese pricing pages.
     The full feature comparison lives in the translated table below, so the cards carry three
     highlights each rather than repeating all thirteen. */
  const tiers = useMemo(() => {
    const free = plans.find((plan) => plan.id === "free");
    const premium = planFor("Premium", cycle);
    const elite = planFor("Elite", cycle);

    return [
      {
        id: "free" as const,
        name: free?.name ?? "Free",
        price: page.free,
        period: "",
        copy: page.plans.free,
        featured: false,
      },
      {
        id: "premium" as const,
        name: premium?.name ?? "Premium",
        price: `£${premium?.price ?? 19}`,
        period: cycle === "monthly" ? page.perMonth : page.perYear,
        copy: page.plans.premium,
        featured: true,
      },
      {
        id: "elite" as const,
        name: elite?.name ?? "Elite",
        price: `£${elite?.price ?? 49}`,
        period: cycle === "monthly" ? page.perMonth : page.perYear,
        copy: page.plans.elite,
        featured: false,
      },
    ];
  }, [cycle, page]);

  const limitRows = useMemo(() => {
    const free = plans.find((plan) => plan.id === "free");
    const premium = planFor("Premium", "monthly");
    const perDay = (plan?: SubscriptionPlan) =>
      plan?.dailyLikesLimit === "unlimited" ? page.unlimited : String(plan?.dailyLikesLimit ?? "");

    return [
      { id: "syncRequests", label: page.limitRows.syncRequests, values: [perDay(free), perDay(premium), page.unlimited] },
      { id: "eventsHosted", label: page.limitRows.eventsHosted, values: ["1", "2", page.unlimited] },
    ];
  }, [page]);

  return (
    <>
      <SiteHero
        eyebrow={page.eyebrow}
        title={page.title}
        lede={page.lede}
        primaryLabel={page.startFree}
        secondaryHref="/features"
        secondaryLabel={c.common.backToFeatures}
      />

      <section className="pb-16 md:pb-24">
        <div className="gs-page">
          {/* Billing cycle. A segmented control rather than a toggle switch: a switch does
              not say which side is on, and yearly is the cheaper option, which is worth
              being unambiguous about. */}
          <div className="flex justify-center">
            <div className="inline-flex items-center rounded-xl border border-neutral-200 bg-white p-1" role="group" aria-label={page.monthly}>
              {(["monthly", "yearly"] as Cycle[]).map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setCycle(option)}
                  aria-pressed={cycle === option}
                  className={cn(
                    "rounded-lg px-5 py-2 text-sm font-bold transition-colors",
                    cycle === option ? "gradient-primary !text-white" : "text-neutral-600 hover:text-neutral-900",
                  )}
                >
                  {option === "monthly" ? page.monthly : page.yearly}
                </button>
              ))}
            </div>
          </div>
          {cycle === "yearly" && (
            <p className="mt-3 text-center text-sm font-semibold text-primary">{page.yearlyNote}</p>
          )}

          <div className="mt-10 grid gap-5 lg:grid-cols-3 lg:items-stretch">
            {tiers.map((tier) => (
              <div
                key={tier.id}
                className={cn(
                  "relative flex min-w-0 flex-col rounded-[1.75rem] border bg-white p-7 md:p-8",
                  tier.featured
                    ? "border-primary/40 shadow-[0_28px_70px_-30px_rgba(11,99,224,0.7)] lg:-mt-4 lg:pb-12"
                    : "border-neutral-200",
                )}
              >
                {/* A div, not a span: globals.css forces `position: relative` onto any span with
                    gradient-primary, which cancelled the `absolute` here and stretched the badge
                    into a full width bar across the top of the card. */}
                {tier.featured && (
                  <div className="absolute -top-3 left-7 w-fit rounded-full gradient-primary px-3 py-1 text-[11px] font-bold uppercase tracking-wider !text-white">
                    {page.mostPopular}
                  </div>
                )}
                <p className="text-[15px] font-bold uppercase tracking-wider text-neutral-500">{tier.name}</p>
                <p className="mt-4 flex items-baseline gap-2">
                  <span className="text-[2.75rem] font-extrabold leading-none tracking-[-0.04em] text-neutral-900">{tier.price}</span>
                  {tier.period && <span className="text-sm text-neutral-500">{tier.period}</span>}
                </p>
                <p className="mt-3 min-h-[3.5rem] text-[14.5px] leading-relaxed text-neutral-600">{tier.copy.description}</p>

                <Link
                  href="/auth/signup"
                  className={buttonClasses({
                    variant: tier.featured ? "primary" : "ghost",
                    size: "md",
                    fullWidth: true,
                    className: "mt-6",
                  })}
                >
                  {tier.id === "free" ? page.startFree : page.choosePlan.replace("{plan}", tier.name)}
                </Link>

                <ul className="mt-7 space-y-2.5 border-t border-neutral-200 pt-6">
                  {tier.copy.highlights.map((highlight) => (
                    <li key={highlight} className="flex items-start gap-2.5">
                      <Check className="mt-1 h-3.5 w-3.5 shrink-0 text-primary" strokeWidth={3} aria-hidden />
                      <span className="text-[14px] leading-relaxed text-neutral-700">{highlight}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-neutral-200 bg-white py-16 md:py-20">
        <div className="gs-page">
          <SectionHead title={page.comparisonHeading} body={page.comparisonNote} />

          {/* Scrolls inside itself on a phone rather than pushing the page sideways. Focusable
              and named so a keyboard user can scroll it too (WCAG 2.1.1, axe scrollable-region-focusable). */}
          <div
            className="mt-9 -mx-6 overflow-x-auto px-6 md:mx-0 md:px-0 rounded-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            tabIndex={0}
            role="region"
            aria-label={page.comparisonHeading}
          >
            <table className="w-full min-w-[38rem] border-collapse text-left">
              <thead>
                <tr className="border-b border-neutral-200">
                  <th scope="col" className="py-3 pr-4 text-[13px] font-semibold text-neutral-400">
                    <span className="sr-only">{page.comparisonHeading}</span>
                  </th>
                  {[page.col.free, page.col.premium, page.col.elite].map((heading) => (
                    <th key={heading} scope="col" className="w-[8.5rem] py-3 pl-4 text-[13px] font-bold uppercase tracking-wider text-neutral-500">
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* The numbers come from lib/data/subscriptions.ts, so a limit change reaches the
                    table without anyone editing translated content, and none of the 21 locales
                    can end up quoting a stale figure. */}
                {limitRows.map((row) => (
                  <tr key={row.id} className="border-b border-neutral-100">
                    <th scope="row" className="py-4 pr-4 text-[14.5px] font-semibold leading-snug text-neutral-800">
                      {row.label}
                    </th>
                    {row.values.map((value, index) => (
                      <td key={index} className="py-4 pl-4 align-middle text-[14px] font-semibold text-neutral-800">
                        {value}
                      </td>
                    ))}
                  </tr>
                ))}
                {TEXT_ROWS.map((id) => {
                  const row = page.textRows[id];
                  return (
                  <tr key={id} className="border-b border-neutral-100">
                    <th scope="row" className="py-4 pr-4 text-[14.5px] font-semibold leading-snug text-neutral-800">
                      {row.label}
                    </th>
                    {[row.free, row.premium, row.elite].map((value, index) => (
                      <td key={index} className="py-4 pl-4 align-middle text-[14px] font-semibold text-neutral-800">
                        {value}
                      </td>
                    ))}
                  </tr>
                  );
                })}
                {CHECK_ROWS.map((row) => (
                  <tr key={row.id} className="border-b border-neutral-100">
                    <th scope="row" className="py-4 pr-4 text-[14.5px] font-semibold leading-snug text-neutral-800">
                      {page.checkRows[row.id]}
                    </th>
                    {row.tiers.map((included, index) => (
                      <td key={index} className="py-4 pl-4 align-middle">
                        {included ? (
                          <Check className="h-[18px] w-[18px] text-primary" strokeWidth={3} aria-label={page.col.free} />
                        ) : (
                          <Minus className="h-4 w-4 text-neutral-300" aria-hidden />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="gs-page grid gap-12 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1fr)] lg:gap-16">
          <div className="min-w-0">
            <SectionHead eyebrow={page.ticketEyebrow} title={page.ticketTitle} body={page.ticketBody} />
          </div>
          <div className="min-w-0">
            <FaqList heading={c.common.faqHeading} items={page.faq} />
          </div>
        </div>
      </section>

      <RelatedLinks
        links={[
          { href: "/features", title: c.nav.allFeatures, body: c.featuresPage.lede.split(".")[0] },
          { href: "/features/events", title: c.features.events.name, body: c.features.events.tagline },
          { href: "/faq", title: c.company.faq, body: c.company.faqTagline },
        ]}
      />

      <CtaBand />
    </>
  );
}
